// src/store/slices/stylistsSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Stylist, Review } from '../../types/stylist';

// Mock data for development
const mockStylists: Stylist[] = [
  {
    _id: 'stylist1',
    id: 'stylist1', // Provide both _id and id for compatibility
    userId: 'user1',
    name: 'Emma Thompson',
    profilePhoto: {
      url: 'https://randomuser.me/api/portraits/women/44.jpg',
      id: 'photo1'
    },
    coverPhoto: 'https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    bio: 'Professional stylist with over 10 years of experience specializing in color and cuts for all hair types.',
    location: {
      address: '123 Main Street',
      city: 'San Francisco',
      state: 'CA',
      zipCode: '94105',
      latitude: 37.7897,
      longitude: -122.3980
    },
    specialties: ['haircut', 'coloring', 'styling'],
    services: ['1', '2', '3'],
    workingHours: [
      { day: 'monday', isOpen: true, openTime: '09:00', closeTime: '17:00' },
      { day: 'tuesday', isOpen: true, openTime: '09:00', closeTime: '17:00' },
      { day: 'wednesday', isOpen: true, openTime: '09:00', closeTime: '17:00' },
      { day: 'thursday', isOpen: true, openTime: '09:00', closeTime: '17:00' },
      { day: 'friday', isOpen: true, openTime: '09:00', closeTime: '17:00' },
      { day: 'saturday', isOpen: true, openTime: '10:00', closeTime: '16:00' },
      { day: 'sunday', isOpen: false }
    ],
    rating: 4.8,
    reviewCount: 124,
    reviews: [
      {
        id: 'review1',
        clientId: 'client1',
        clientName: 'John Doe',
        clientPhoto: 'https://randomuser.me/api/portraits/men/75.jpg',
        rating: 5,
        comment: 'Emma is amazing! She really understood what I wanted and delivered a perfect cut.',
        date: '2025-03-15T14:30:00Z'
      },
      {
        id: 'review2',
        clientId: 'client2',
        clientName: 'Sarah Johnson',
        clientPhoto: 'https://randomuser.me/api/portraits/women/28.jpg',
        rating: 4,
        comment: 'Great service, very professional, and my color looks fantastic.',
        date: '2025-03-01T10:15:00Z'
      }
    ],
    portfolio: [
      {
        id: 'portfolio1',
        image: 'https://images.unsplash.com/photo-1519699047748-de8e457a634e?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
        description: 'Balayage on medium length hair',
        tags: ['balayage', 'medium length', 'brown hair'],
        createdAt: '2025-01-15T09:30:00Z'
      },
      {
        id: 'portfolio2',
        image: 'https://images.unsplash.com/photo-1562594122-abb98926ed81?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
        description: 'Modern short cut with texture',
        tags: ['short hair', 'texture', 'modern'],
        createdAt: '2025-02-05T14:45:00Z'
      }
    ],
    isAvailable: true,
    createdAt: '',
    updatedAt: ''
  },
  {
    _id: 'stylist2',
    id: 'stylist2',
    userId: 'user2',
    name: 'Michael Chen',
    profilePhoto: {
      url: 'https://randomuser.me/api/portraits/men/32.jpg',
      id: 'photo2'
    },
    coverPhoto: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    bio: 'Award-winning barber and stylist specializing in men\'s grooming, fades, and beard styling.',
    location: {
      address: '456 Market Street',
      city: 'San Francisco',
      state: 'CA',
      zipCode: '94103',
      latitude: 37.7793,
      longitude: -122.4192
    },
    specialties: ['haircut', 'beard trim', 'fades'],
    services: ['1', '4'],
    workingHours: [
      { day: 'monday', isOpen: false },
      { day: 'tuesday', isOpen: true, openTime: '10:00', closeTime: '18:00' },
      { day: 'wednesday', isOpen: true, openTime: '10:00', closeTime: '18:00' },
      { day: 'thursday', isOpen: true, openTime: '10:00', closeTime: '18:00' },
      { day: 'friday', isOpen: true, openTime: '10:00', closeTime: '18:00' },
      { day: 'saturday', isOpen: true, openTime: '09:00', closeTime: '17:00' },
      { day: 'sunday', isOpen: true, openTime: '12:00', closeTime: '16:00' }
    ],
    rating: 4.9,
    reviewCount: 98,
    reviews: [
      {
        id: 'review3',
        clientId: 'client3',
        clientName: 'David Smith',
        clientPhoto: 'https://randomuser.me/api/portraits/men/41.jpg',
        rating: 5,
        comment: 'Mike gave me the best fade I\'ve ever had. Highly recommend!',
        date: '2025-03-10T16:45:00Z'
      }
    ],
    portfolio: [
      {
        id: 'portfolio3',
        image: 'https://images.unsplash.com/photo-1503951914875-452162b0f3f1?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
        description: 'Clean fade with beard trim',
        tags: ['fade', 'beard', 'men'],
        createdAt: '2025-02-18T11:30:00Z'
      }
    ],
    isAvailable: true,
    createdAt: '',
    updatedAt: ''
  },
  {
    _id: 'stylist3',
    id: 'stylist3',
    userId: 'user3',
    name: 'Lisa Wong',
    profilePhoto: {
      url: 'https://randomuser.me/api/portraits/women/63.jpg',
      id: 'photo3'
    },
    coverPhoto: 'https://images.unsplash.com/photo-1527799820374-dcf8d9d4a388?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    bio: 'Color specialist with expertise in balayage, highlights, and creative color techniques.',
    location: {
      address: '789 Mission Street',
      city: 'San Francisco',
      state: 'CA',
      zipCode: '94103',
      latitude: 37.7845,
      longitude: -122.4051
    },
    specialties: ['coloring', 'balayage', 'highlights'],
    services: ['3'],
    workingHours: [
      { day: 'monday', isOpen: true, openTime: '11:00', closeTime: '19:00' },
      { day: 'tuesday', isOpen: true, openTime: '11:00', closeTime: '19:00' },
      { day: 'wednesday', isOpen: true, openTime: '11:00', closeTime: '19:00' },
      { day: 'thursday', isOpen: true, openTime: '11:00', closeTime: '19:00' },
      { day: 'friday', isOpen: true, openTime: '11:00', closeTime: '19:00' },
      { day: 'saturday', isOpen: false },
      { day: 'sunday', isOpen: false }
    ],
    rating: 4.7,
    reviewCount: 86,
    reviews: [
      {
        id: 'review4',
        clientId: 'client4',
        clientName: 'Jennifer Lee',
        clientPhoto: 'https://randomuser.me/api/portraits/women/55.jpg',
        rating: 5,
        comment: 'Lisa is a color genius! My balayage looks incredible and she was so attentive.',
        date: '2025-03-05T13:20:00Z'
      }
    ],
    portfolio: [
      {
        id: 'portfolio4',
        image: 'https://images.unsplash.com/photo-1527782418766-ee0cea5e6127?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
        description: 'Platinum blonde transformation',
        tags: ['blonde', 'transformation', 'platinum'],
        createdAt: '2025-01-25T10:15:00Z'
      },
      {
        id: 'portfolio5',
        image: 'https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
        description: 'Red copper balayage',
        tags: ['balayage', 'red', 'copper'],
        createdAt: '2025-02-10T15:30:00Z'
      }
    ],
    isAvailable: true,
    createdAt: '',
    updatedAt: ''
  }
];

interface StylistsState {
  stylists: Stylist[];
  filteredStylists: Stylist[];
  currentStylist: Stylist | null;
  isLoading: boolean;
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  filters: {
    specialties: string[];
    minRating: number | null;
    maxDistance: number | null;
    searchTerm: string;
    sortBy: 'rating' | 'distance' | 'alphabetical' | null;
  };
}

const initialState: StylistsState = {
  stylists: [],
  filteredStylists: [],
  currentStylist: null,
  isLoading: false,
  error: null,
  status: 'idle',
  filters: {
    specialties: [],
    minRating: null,
    maxDistance: null,
    searchTerm: '',
    sortBy: null
  }
};

// Async thunks
export const fetchStylists = createAsyncThunk(
  'stylists/fetchAll',
  async (params: { location?: string, specialties?: string[], maxDistance?: number } = {}, { rejectWithValue }) => {
    try {
      // In a real app, this would be an API call with query params
      // const response = await apiClient.get('/stylists', { params });
      // return response.data;
      
      // Using mock data for development
      // Here you could filter based on params
      return mockStylists;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch stylists');
    }
  }
);

export const fetchStylistById = createAsyncThunk(
  'stylists/fetchById',
  async (stylistId: string, { rejectWithValue }) => {
    try {
      // In a real app, this would be an API call
      // const response = await apiClient.get(`/stylists/${stylistId}`);
      // return response.data;
      
      // Using mock data for development
      const stylist = mockStylists.find(s => s._id === stylistId || s.id === stylistId);
      if (!stylist) {
        throw new Error('Stylist not found');
      }
      return stylist;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch stylist');
    }
  }
);

export const fetchStylistReviews = createAsyncThunk(
  'stylists/fetchReviews',
  async (stylistId: string, { rejectWithValue }) => {
    try {
      // In a real app, this would be an API call
      // const response = await apiClient.get(`/stylists/${stylistId}/reviews`);
      // return response.data;
      
      // Using mock data for development
      const stylist = mockStylists.find(s => s.id === stylistId);
      if (!stylist) {
        throw new Error('Stylist not found');
      }
      return stylist.reviews;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch reviews');
    }
  }
);

export const fetchStylistPortfolio = createAsyncThunk(
  'stylists/fetchPortfolio',
  async (stylistId: string, { rejectWithValue }) => {
    try {
      // In a real app, this would be an API call
      // const response = await apiClient.get(`/stylists/${stylistId}/portfolio`);
      // return response.data;
      
      // Using mock data for development
      const stylist = mockStylists.find(s => s.id === stylistId);
      if (!stylist) {
        throw new Error('Stylist not found');
      }
      return stylist.portfolio;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch portfolio');
    }
  }
);

export const addReview = createAsyncThunk(
  'stylists/addReview',
  async ({ 
    stylistId, 
    review 
  }: { 
    stylistId: string, 
    review: { rating: number, comment: string } 
  }, { rejectWithValue }) => {
    try {
      // In a real app, this would be an API call
      // const response = await apiClient.post(`/stylists/${stylistId}/reviews`, review);
      // return response.data;
      
      // Using mock data for development
      const stylistIndex = mockStylists.findIndex(s => s._id === stylistId || s.id === stylistId);
      if (stylistIndex === -1) {
        throw new Error('Stylist not found');
      }
      
      const newReview: Review = {
        id: `review${Date.now()}`,
        clientId: 'client1', // This would come from the authenticated user
        clientName: 'John Doe', // This would come from the authenticated user
        clientPhoto: 'https://randomuser.me/api/portraits/men/75.jpg', // This would come from the authenticated user
        rating: review.rating,
        comment: review.comment,
        date: new Date().toISOString()
      };
      
      mockStylists[stylistIndex].reviews.push(newReview);
      
      // Recalculate rating
      const reviews = mockStylists[stylistIndex].reviews;
      const totalRating = reviews.reduce((sum, r) => sum + r.rating, 0);
      mockStylists[stylistIndex].rating = totalRating / reviews.length;
      mockStylists[stylistIndex].reviewCount = reviews.length;
      
      return {
        stylist: mockStylists[stylistIndex],
        review: newReview
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add review');
    }
  }
);

const stylistsSlice = createSlice({
  name: 'stylists',
  initialState,
  reducers: {
    clearCurrentStylist: (state) => {
      state.currentStylist = null;
    },
    filterStylistsBySpecialty: (state, action: PayloadAction<string[]>) => {
      state.filters.specialties = action.payload;
      
      if (action.payload.length === 0) {
        // No filters, show all stylists
        state.filteredStylists = state.stylists;
        return;
      }
      
      // This is just client-side filtering for development
      // In a real app, you'd likely refetch with filters
      const specialties = action.payload;
      state.filteredStylists = state.stylists.filter(stylist => 
        stylist.specialties.some(specialty => specialties.includes(specialty))
      );
    },
    filterStylistsByRating: (state, action: PayloadAction<number>) => {
      state.filters.minRating = action.payload;
      
      state.filteredStylists = state.stylists.filter(stylist => 
        stylist.rating >= action.payload
      );
    },
    filterStylistsByDistance: (state, action: PayloadAction<number>) => {
      state.filters.maxDistance = action.payload;
      
      if (state.stylists.some(s => s.distance !== undefined)) {
        state.filteredStylists = state.stylists.filter(stylist => 
          stylist.distance !== undefined && stylist.distance <= action.payload
        );
      }
    },
    searchStylists: (state, action: PayloadAction<string>) => {
      state.filters.searchTerm = action.payload;
      
      if (!action.payload) {
        // If search term is empty, reset to all stylists
        state.filteredStylists = state.stylists;
        return;
      }
      
      // Search by stylist name (case insensitive)
      const searchTerm = action.payload.toLowerCase();
      state.filteredStylists = state.stylists.filter(stylist => 
        stylist.name.toLowerCase().includes(searchTerm) ||
        stylist.specialties.some(specialty => specialty.toLowerCase().includes(searchTerm))
      );
    },
    sortStylists: (state, action: PayloadAction<'rating' | 'distance'>) => {
      state.filters.sortBy = action.payload;
      
      if (action.payload === 'rating') {
        state.filteredStylists.sort((a, b) => b.rating - a.rating);
      } else if (action.payload === 'distance') {
        state.filteredStylists.sort((a, b) => {
          // Handle undefined distances
          if (a.distance === undefined) return 1;
          if (b.distance === undefined) return -1;
          return a.distance - b.distance;
        });
      }
    },
    clearFilters: (state) => {
      state.filters = {
        specialties: [],
        minRating: null,
        maxDistance: null,
        searchTerm: '',
        sortBy: null
      };
      state.filteredStylists = state.stylists;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchStylists
      .addCase(fetchStylists.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchStylists.fulfilled, (state, action: PayloadAction<Stylist[]>) => {
        state.isLoading = false;
        state.stylists = action.payload;
        state.filteredStylists = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchStylists.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.status = 'failed';
      })
      
      // fetchStylistById
      .addCase(fetchStylistById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchStylistById.fulfilled, (state, action: PayloadAction<Stylist>) => {
        state.isLoading = false;
        state.currentStylist = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchStylistById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.status = 'failed';
      })
      
      // addReview
      .addCase(addReview.fulfilled, (state, action) => {
        if (state.currentStylist && 
          (state.currentStylist._id === action.payload.stylist._id || 
           state.currentStylist.id === action.payload.stylist.id)) {
          state.currentStylist = action.payload.stylist;
        }
        
        // Update the stylist in the stylists array
        const index = state.stylists.findIndex(s => 
          s._id === action.payload.stylist._id || 
          s.id === action.payload.stylist.id
        );
        if (index !== -1) {
          state.stylists[index] = action.payload.stylist;
        }
      });
  },
});

export const { 
  clearCurrentStylist,
  filterStylistsBySpecialty,
  filterStylistsByRating,
  filterStylistsByDistance,
  searchStylists,
  sortStylists,
  clearFilters
} = stylistsSlice.actions;

export default stylistsSlice.reducer;